import { useEffect, useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { PATHS } from '@belong/common';
import { FoundersFeedbackCard, Image, Link, Text } from '@belong/ui';
import { useFlows } from 'accounts/hooks/homeowners/use-flows';
import { getActionItems } from 'action-items/utils';
import { UserModel, WealthInfo } from 'api/models';
import classNames from 'classnames/bind';
import { FixedMenu } from 'components/FixedMenu/FixedMenu';
import Menu from 'components/Menu/Menu';
import { MENU_TYPES } from 'components/Menu/menuTypes';
import {
  HOMEOWNER_ACCOUNT_MENU_ITEMS,
  ALL_USER_MENU,
} from 'consts/account-menu-items';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { useFullManagementHomes } from 'hooks/useFullManagementHomes';
import { BASE_PATHS } from 'routes/paths';
import { fetchWealth } from 'store/redux/homeowner-accounts/actions';
import {
  selectAccountProperties,
  selectAccountPropertySummaries,
} from 'store/redux/homeowner-accounts/selectors';
import { selectInspections } from 'store/redux/inspections';
import { selectSettings } from 'store/redux/settings';
import { selectUserToDos, selectUser } from 'store/redux/user/selectors';
import { isPriceDropsEnabled } from 'utils/featureFlags';
import styles from './accounts-layout.module.css';

const cx = classNames.bind(styles);

type Props = {
  comeFromTheMobileApp?: boolean;
  isResident: boolean;
  children?: ReactNode;
};

export function HomeownersLayout({
  comeFromTheMobileApp,
  isResident,
  children,
}: Props) {
  const [isSidebarScrolled, setIsSidebarScrolled] = useState(false);
  const dispatch = useDispatch();
  const homeownerProperties = useSelector(selectAccountProperties);
  const accountPropertySummaries = useSelector(selectAccountPropertySummaries);
  const { flows } = useFlows();
  const [wealth, setWealth] = useState<WealthInfo[]>();
  const settings = useSelector(selectSettings);
  const todos = useSelector(selectUserToDos);
  const user = useSelector(selectUser) as unknown as UserModel;
  const inspections = useSelector(selectInspections);

  let actionItems = [];

  if (
    Array.isArray(accountPropertySummaries) &&
    Array.isArray(flows) &&
    Array.isArray(todos) &&
    Array.isArray(inspections) &&
    typeof user !== 'undefined'
  ) {
    actionItems = getActionItems({
      properties: accountPropertySummaries,
      flows,
      todos,
      user,
      inspections,
      // `getActionItems` declares this required, but every read of it inside is
      // `wealth?.find(...)`, so `[]` and `undefined` produce the same result.
      wealth: wealth ?? [],
    });
  }

  const hasActionItems = actionItems.length > 0;

  const { isEnabled: isAccountRedesignEnabled } =
    useFeatureFlag('account-redesign');

  const fullManagementHomes = useFullManagementHomes();

  const params = useParams<Record<string, string>>();
  const { section, subSection, propertyId } = params;

  // The menu entries come from `consts/account-menu-items.js`, and these arrays
  // mix whole entries, `.find()` results (so possibly `undefined` — see the
  // `hasMissingMobileMenuItems` guard below) and spread-and-extend objects. There
  // is no single shape to target, and the consumers (`Menu`, `FixedMenu`) are JS
  // components validated by PropTypes.
  const desktopMenuItems: any[] = [];
  let commonMenuItems: any[] = [];
  let mobileMenu: any[] = [];
  const inboxMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
    (item) => item.key === 'inbox',
  );
  const allUserMenu = [
    ...(inboxMenuItem ? [inboxMenuItem] : []),
    ...ALL_USER_MENU,
  ];

  const actionItemsMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
    (item) => item.key === 'action-items',
  );

  if (isAccountRedesignEnabled) {
    desktopMenuItems.push({
      ...actionItemsMenuItem,
      header: hasActionItems
        ? `Action Items (${actionItems?.length})`
        : 'Action Items',
      badgeCount: actionItems?.length,
    });
  }

  const hasFullListingUnit = homeownerProperties?.some((property) =>
    property.units?.some(
      (unit: any) =>
        unit.basicInfo?.currentMilestone === 'FullListingPublished' ||
        unit.basicInfo?.currentMilestone === 'ListingRepublished',
    ),
  );

  if (homeownerProperties?.length) {
    const hasPrimaryOwnerHomes = homeownerProperties.some(
      (property) => property.getPrimaryOwnerUnits().length,
    );

    const propertiesMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
      (item) => item.key === 'properties',
    );

    mobileMenu.push(propertiesMenuItem);

    const propertiesMenuItems: any[] = [];

    homeownerProperties.forEach((property) => {
      if (property.getUnitsWithAgreements().length) {
        // Check if it has incomplete setup flow
        const isSetupFlowIncomplete = property.units.some(
          (unit: any) => !unit.isSetupFlowCompleted,
        );

        propertiesMenuItems.push({
          header: property.address.streetAddress,
          key: property.basicInfo.propertyId,
          to: `${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${property.basicInfo.propertyId}`,
          redIndicator: isSetupFlowIncomplete && !isAccountRedesignEnabled,
        });
      }
    });

    if (hasPrimaryOwnerHomes) {
      if (isPriceDropsEnabled && hasFullListingUnit) {
        const listingPerformanceMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
          (item) => item.key === 'listing-performance',
        );

        commonMenuItems.push(listingPerformanceMenuItem);
      }

      const maintenanceMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
        (item) => item.key === 'maintenance',
      );
      const earningsMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
        (item) => item.key === 'earnings',
      );

      commonMenuItems.push(maintenanceMenuItem);
      commonMenuItems.push(earningsMenuItem);
    }

    const wealthMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
      (item) => item.key === 'wealth',
    );

    commonMenuItems.push({
      ...wealthMenuItem,
      new: wealth?.some((item: any) => item.flow?.status !== 'Cancelled'),
    });

    const homeownerPropertiesStates = Array.from(
      new Set(
        homeownerProperties.map(
          (homeownerProperty) => homeownerProperty.address?.state as string,
        ),
      ),
    );

    const isAtLeastOneHomeWithinInsuraceCoverage =
      homeownerPropertiesStates.filter(
        (homeownerPropertyState) =>
          settings?.licenses?.[homeownerPropertyState]?.Insurance
            ?.belongLicenseNumber,
      );

    if (isAtLeastOneHomeWithinInsuraceCoverage.length) {
      const insuranceMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
        (item) => item.key === 'insurance',
      );

      commonMenuItems.push(insuranceMenuItem);
    }

    const documentsMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
      (item) => item.key === 'documents',
    );

    commonMenuItems.push(documentsMenuItem);

    if (fullManagementHomes?.length === 0) {
      commonMenuItems = commonMenuItems.filter(
        (item) => item.key !== 'maintenance',
      );
    }

    const hasMultipleHomes =
      homeownerProperties.flatMap((property) => property.units).length > 1;

    desktopMenuItems.push({
      header: hasMultipleHomes ? 'My Homes' : 'My Home',
      key: 'portfolio',
      menuType: MENU_TYPES.BORDER,
      selected: subSection,
      alwaysOpen: true,
      to: PATHS.HOMEOWNERS_ACCOUNT_PROPERTIES,
      menuItems: [
        {
          header: '',
          menuHideOverFive: true,
          menuType: MENU_TYPES.BORDER_NESTED,
          alwaysOpen: true,
          selected: propertyId,
          menuItems: propertiesMenuItems,
        },
        ...commonMenuItems,
      ],
    });
  }

  mobileMenu.push(...commonMenuItems);

  // `resident-account` is a static entry in `HOMEOWNER_ACCOUNT_MENU_ITEMS`, so
  // this lookup always succeeds — `.find()`'s signature just can't say so.
  const residentAccountMenuItem = HOMEOWNER_ACCOUNT_MENU_ITEMS.find(
    (item) => item.key === 'resident-account',
  )!;

  // In mobile, we don't want Listing Performance to be in the fixed bottom bar
  if (isPriceDropsEnabled && hasFullListingUnit) {
    const itemToMove = mobileMenu.splice(1, 1)[0];

    if (itemToMove) {
      mobileMenu.splice(3, 0, itemToMove);
    }
  }

  if (isAccountRedesignEnabled) {
    const [first, second, third, ...rest] = mobileMenu;

    mobileMenu = [
      first,
      second,
      third,
      {
        ...actionItemsMenuItem,
        badgeCount: actionItems?.length,
      },
      ...rest,
    ];
  }

  useEffect(() => {
    async function init() {
      // The promise middleware resolves `dispatch` to the API response; the
      // `Dispatch` type models it as returning the action object it was given.
      const wealthData = (await dispatch(
        fetchWealth(),
      )) as unknown as WealthInfo[];

      setWealth(wealthData);
    }

    init();
  }, [dispatch]);

  const hasMissingMobileMenuItems = mobileMenu.some((item) => !item);

  if (hasMissingMobileMenuItems) {
    return null;
  }

  return (
    <>
      {!comeFromTheMobileApp && (
        <FixedMenu
          menuItems={[
            ...mobileMenu,
            ...allUserMenu,
            ...(isResident ? [residentAccountMenuItem] : []),
          ]}
          params={params}
        />
      )}
      <div className="container mt-sm pb-3xl lg:mt-lg lg:pb-0">
        <div className={cx('sidebar-wrapper')}>
          <div
            className={cx('sidebar', {
              'sidebar-scrolled': isSidebarScrolled,
            })}
            onScroll={(event) =>
              setIsSidebarScrolled(event.currentTarget.scrollTop > 0)
            }
          >
            <Menu
              controlled
              menuType={MENU_TYPES.BACKGROUND}
              selected={section}
              menuItems={[...desktopMenuItems, ...allUserMenu]}
            />
            <div className="hidden flex-col lg:flex">
              {isResident && (
                <div className="py-xs pl-sm">
                  <Link href={residentAccountMenuItem.to}>
                    <div className="flex items-center">
                      <div className={styles.switchAccountContainer}>
                        <Image src="/accounts/switch-account-icon.svg" alt="" />
                      </div>
                      <Text fontWeight="semibold" className="ml-xs normal-case">
                        {residentAccountMenuItem.header}
                      </Text>
                    </div>
                  </Link>
                </div>
              )}
              <div className="pt-xl">
                <FoundersFeedbackCard name="founders_feedback_homeowner_sidebar_legacy" />
              </div>
            </div>
          </div>
        </div>
        {children}
      </div>
    </>
  );
}
